import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import GlobalHeader from "../components/layout/GlobalHeader";
import EmptyStateAddBusiness from "../components/EmptyStateAddBusiness";
import { t, getLocale, setLocale } from "../lib/i18n";
import { StarRating } from "./components/StarRating";
import { useAuth } from "../lib/auth";
import { useCustomerAuth, customerApi } from "../lib/customerAuth";
import { track } from "../lib/analytics";
import { slugifyCity } from "../lib/seo";
import { sectorPlaceholderIcon } from "../lib/sectorPlaceholder";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import {
  MagnifyingGlass, MapPin, Storefront, ArrowRight, X, Sparkle, GlobeHemisphereWest, Images,
  InstagramLogo, FacebookLogo, GoogleLogo, Briefcase, Heart, Scales, ListBullets, MapTrifold,
  Star, Crown, Wrench, Clock, CheckCircle,
  SlidersHorizontal, Funnel,
} from "@phosphor-icons/react";

const pub = axios.create({ baseURL: `${BACKEND_URL}/api` });

// ------- Leaflet default-marker icon (CRA-friendly) ------------------------
const DefaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// ------- Favorites hook (localStorage + sync customer-area) ----------------
const FAV_KEY = "ag_favorites";
function useFavorites() {
  const { account } = useCustomerAuth() || { account: null };
  const [favs, setFavs] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || "[]")); }
    catch { return new Set(); }
  });
  useEffect(() => { localStorage.setItem(FAV_KEY, JSON.stringify([...favs])); }, [favs]);
  // Si hay cliente autenticado, sincronizar con backend al montar.
  useEffect(() => {
    if (!account) return;
    customerApi.get("/customer-area/favorites").then(({ data }) => {
      const remoteIds = (data.items || []).map((i) => i.id);
      setFavs((prev) => new Set([...prev, ...remoteIds]));
    }).catch(() => {});
  }, [account]);
  const toggle = useCallback(async (orgId) => {
    const wasFav = favs.has(orgId);
    const next = new Set(favs);
    if (wasFav) next.delete(orgId); else next.add(orgId);
    setFavs(next);
    if (account) {
      try {
        if (wasFav) await customerApi.delete(`/customer-area/favorites/${orgId}`);
        else await customerApi.post(`/customer-area/favorites/${orgId}`);
      } catch {}
    }
  }, [favs, account]);
  return { favs, toggle };
}

// ------- Sprint S-5: Compare bar (max 3) -----------------------------------
function useCompare() {
  const [ids, setIds] = useState([]);
  const toggle = useCallback((id) => {
    setIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : (prev.length >= 3 ? prev : [...prev, id]));
  }, []);
  const clear = useCallback(() => setIds([]), []);
  return { ids, toggle, clear, has: (id) => ids.includes(id) };
}

// ------- CategoryIcon helper -----------------------------------------------
const CAT_ICONS = { Star, Sparkle, Crown, Wrench };
const CAT_ICON = (name) => CAT_ICONS[name] || Sparkle;

export default function Marketplace() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "");
  const [availability, setAvailability] = useState({});  // {orgId: bool}
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [city, setCity] = useState(searchParams.get("city") || "");
  const [locality, setLocality] = useState(searchParams.get("locality") || "");
  const [sector, setSector] = useState(searchParams.get("sector") || "");
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");        // "list" | "map"
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareData, setCompareData] = useState([]);
  const [locale, setLoc] = useState(getLocale());
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "relevance");  // relevance | rating | recent
  const { favs, toggle: toggleFav } = useFavorites();
  const { ids: compareIds, toggle: toggleCompare, clear: clearCompare, has: isInCompare } = useCompare();
  const avAbortRef = useRef(null);
  useEffect(() => { setLocale(locale); }, [locale]);
  const tr = (k) => t(k, locale);

  // ------- Load orgs (con categoría y filtros) ----
  const load = useCallback(async (qVal, cityVal, locVal, sectorVal, catVal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (qVal) params.set("q", qVal);
      if (cityVal) params.set("city", cityVal);
      if (locVal) params.set("locality", locVal);
      if (sectorVal) params.set("sector", sectorVal);
      if (catVal) params.set("category", catVal);
      params.set("limit", "60");
      const { data } = await pub.get(`/marketplace/orgs?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    track("marketplace_view");
    pub.get("/marketplace/cities").then((r) => setCities(r.data.cities || []));
    pub.get("/marketplace/localities").then((r) => setLocalities(r.data.localities || []));
    pub.get("/catalog/sectors").then((r) => setSectors(r.data.sectors || [])).catch(() => {});
    pub.get("/marketplace/categories").then((r) => setCategories(r.data.items || [])).catch(() => {});
    // Fase 2A · P0-2 · Cargar con filtros iniciales de la URL
    load(
      searchParams.get("q") || "",
      searchParams.get("city") || "",
      searchParams.get("locality") || "",
      searchParams.get("sector") || "",
      searchParams.get("category") || "",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Fase 2A · P0-2 · URL state · sincroniza filtros → querystring (shareable)
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (city) next.set("city", city);
    if (locality) next.set("locality", locality);
    if (sector) next.set("sector", sector);
    if (activeCategory) next.set("category", activeCategory);
    if (sortBy && sortBy !== "relevance") next.set("sort", sortBy);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, city, locality, sector, activeCategory, sortBy]);

  // Reactivo: al cambiar ciudad, refresca la lista de localidades disponibles
  useEffect(() => {
    pub.get(`/marketplace/localities${city ? `?city=${encodeURIComponent(city)}` : ""}`)
       .then((r) => setLocalities(r.data.localities || []));
    if (locality && !city) setLocality("");
  }, [city]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Sprint S-5: cargar disponibilidad para las orgs visibles
  useEffect(() => {
    if (!items.length) return;
    if (avAbortRef.current) avAbortRef.current.abort();
    const controller = new AbortController();
    avAbortRef.current = controller;
    const orgIds = items.map((o) => o.id);
    pub.post("/marketplace/availability", { org_ids: orgIds }, { signal: controller.signal })
       .then(({ data }) => setAvailability(data.items || {}))
       .catch(() => {});
    return () => controller.abort();
  }, [items]);

  const onSearch = (e) => {
    e?.preventDefault?.();
    load(q.trim(), city.trim(), locality.trim(), sector, activeCategory);
  };

  // Auto-refresh cada 30s + sync entre pestañas (igual que antes)
  useEffect(() => {
    const interval = setInterval(() => {
      load(q.trim(), city.trim(), locality.trim(), sector, activeCategory);
    }, 30000);
    const onOrgUpdate = () => load(q.trim(), city.trim(), locality.trim(), sector, activeCategory);
    window.addEventListener("ag_org_updated", onOrgUpdate);
    let bc = null;
    try {
      bc = new BroadcastChannel("ag_org");
      bc.onmessage = (ev) => { if (ev?.data?.type === "updated") onOrgUpdate(); };
    } catch (e) {}
    return () => {
      clearInterval(interval);
      window.removeEventListener("ag_org_updated", onOrgUpdate);
      if (bc) { try { bc.close(); } catch (e) {} }
    };
  // eslint-disable-next-line
  }, [q, city, locality, sector, activeCategory]);

  const clearFilters = () => {
    setQ(""); setCity(""); setLocality(""); setSector(""); setActiveCategory("");
    load("", "", "", "", "");
  };

  // ------- Compare modal data ----
  const openCompare = async () => {
    if (compareIds.length < 1) return;
    try {
      const { data } = await pub.get(`/marketplace/orgs/compare?ids=${compareIds.join(",")}`);
      setCompareData(data.items || []);
      setShowCompareModal(true);
      track("marketplace_compare_open", { n: compareIds.length });
    } catch {}
  };

  // ------- Map: items con coords -----
  const mapItems = useMemo(() => items.filter((o) => o.latitude && o.longitude), [items]);
  const mapCenter = useMemo(() => {
    if (mapItems.length) return [mapItems[0].latitude, mapItems[0].longitude];
    return [40.4168, -3.7038]; // Madrid centro por defecto
  }, [mapItems]);

  return (
    <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)] flex flex-col">
      <GlobalHeader variant="auto" testId="marketplace-header" />

      {/* Hero + search */}
      <section className="ag-grid border-b border-[var(--ag-border)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[var(--ag-gold)]/30 rounded-full text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">
            <GlobeHemisphereWest size={11} weight="fill" /> {tr("hero.tagline.eyebrow")}
          </div>
          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mt-5 leading-tight">
            {tr("marketplace.title")}
          </h1>
          <p className="text-[var(--ag-text-dim)] mt-5 max-w-xl mx-auto">
            {tr("marketplace.subtitle")}
          </p>

          <form onSubmit={onSearch} className="mt-7 sm:mt-9 max-w-4xl mx-auto bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded-md p-2.5 hidden sm:grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] gap-2 landscape:grid-cols-[1fr_auto_auto_auto_auto]" data-testid="marketplace-search">
            <div className="relative flex-1 col-span-1 sm:col-auto">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
              <Input
                value={q} onChange={(e)=>setQ(e.target.value)}
                placeholder={tr("marketplace.search_placeholder")}
                className="pl-9 border-0 bg-transparent focus-visible:ring-0 h-11 w-full"
                data-testid="marketplace-q"
              />
            </div>
            <div className="relative sm:w-40 lg:w-44">
              <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)] pointer-events-none" />
              <select
                value={city} onChange={(e)=>setCity(e.target.value)}
                className="w-full pl-9 pr-3 h-11 bg-transparent text-[var(--ag-text)] border-0 outline-none focus:ring-0 text-sm"
                data-testid="marketplace-city">
                <option value="" style={{ background: "#1F1F1F" }}>Todas las ciudades</option>
                {cities.map((c) => (
                  <option key={c} value={c} style={{ background: "#1F1F1F" }}>{c}</option>
                ))}
              </select>
            </div>
            <div className="relative sm:w-40 lg:w-44">
              <select
                value={locality} onChange={(e)=>setLocality(e.target.value)}
                disabled={!localities.length}
                className="w-full px-3 h-11 bg-transparent text-[var(--ag-text)] border-0 outline-none focus:ring-0 text-sm disabled:opacity-40"
                data-testid="marketplace-locality">
                <option value="" style={{ background: "#1F1F1F" }}>Toda la localidad</option>
                {localities.map((l) => (
                  <option key={l} value={l} style={{ background: "#1F1F1F" }}>{l}</option>
                ))}
              </select>
            </div>
            <div className="relative sm:w-40 lg:w-44">
              <select
                value={sector} onChange={(e)=>{ setSector(e.target.value); setTimeout(() => load(q.trim(), city.trim(), locality.trim(), e.target.value, activeCategory), 0); }}
                className="w-full px-3 h-11 bg-transparent text-[var(--ag-text)] border-0 outline-none focus:ring-0 text-sm"
                data-testid="marketplace-sector">
                <option value="" style={{ background: "#1F1F1F" }}>Todos los subsectores</option>
                {sectors.map((s) => (
                  <option key={s.id} value={s.id} style={{ background: "#1F1F1F" }}>{s.label_es}</option>
                ))}
              </select>
            </div>
            <Button type="submit" className="ag-btn-gold h-11 px-5 w-full sm:w-auto" data-testid="marketplace-search-btn">
              <MagnifyingGlass size={16} weight="bold" className="mr-1.5" /> Buscar
            </Button>
          </form>

          {(q || city || locality || sector || activeCategory) && (
            <button onClick={clearFilters} className="mt-3 inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-white" data-testid="marketplace-clear">
              <X size={12} /> Quitar filtros
            </button>
          )}

          {/* Fase 2A · P0-2 · Mobile search bar (compact) + Filters sheet */}
          <div className="sm:hidden mt-6 max-w-md mx-auto space-y-2" data-testid="marketplace-mobile-bar">
            <form onSubmit={onSearch} className="flex items-center gap-2 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded-md p-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={tr("marketplace.search_placeholder")}
                  className="pl-9 border-0 bg-transparent focus-visible:ring-0 h-10 w-full"
                  data-testid="marketplace-mobile-q"
                />
              </div>
              <Button type="submit" className="ag-btn-gold h-10 px-3" data-testid="marketplace-mobile-search-btn">
                <MagnifyingGlass size={14} weight="bold" />
              </Button>
            </form>
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-10 bg-transparent border-[var(--ag-border)] text-[var(--ag-text)] hover:bg-[var(--ag-midnight)] rounded-md justify-center gap-2"
                  data-testid="marketplace-mobile-filters-btn"
                >
                  <SlidersHorizontal size={14} weight="bold" />
                  Filtros
                  {(city || locality || sector || sortBy !== "relevance") && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 bg-[var(--ag-gold)] text-[var(--ag-midnight)] rounded-full text-[10px] font-bold tabular-nums">
                      {[city, locality, sector, sortBy !== "relevance" ? "sort" : ""].filter(Boolean).length}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="bg-[var(--ag-midnight)] border-t border-[var(--ag-border)] text-[var(--ag-text)] max-h-[85vh] overflow-y-auto" data-testid="marketplace-mobile-filters-sheet">
                <SheetHeader className="pb-4 border-b border-[var(--ag-border)]">
                  <SheetTitle className="text-left font-display text-xl font-black tracking-tighter">
                    Filtros
                  </SheetTitle>
                </SheetHeader>
                <MobileFiltersBody
                  cities={cities}
                  localities={localities}
                  sectors={sectors}
                  city={city} setCity={setCity}
                  locality={locality} setLocality={setLocality}
                  sector={sector} setSector={setSector}
                  sortBy={sortBy} setSortBy={setSortBy}
                  total={total}
                  onApply={() => {
                    setMobileFiltersOpen(false);
                    load(q.trim(), city.trim(), locality.trim(), sector, activeCategory);
                  }}
                  onClear={() => {
                    clearFilters();
                    setSortBy("relevance");
                    setMobileFiltersOpen(false);
                  }}
                />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </section>

      {/* Sprint S-5 · Categorías curadas · Sprint P.1.5 Bloque F (CP4):
          ocultar chips con count=0 (CTAs muertos que dañan trust). Si TODAS las
          categorías vienen a 0 → ocultar el bloque completo "Selecciones". */}
      {(() => {
        const nonEmpty = (categories || []).filter((c) => Number(c.count) > 0);
        if (nonEmpty.length === 0) return null;
        return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-7">
          <div className="flex flex-wrap items-center gap-2" data-testid="marketplace-categories">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] mr-2">Selecciones</span>
            <button
              onClick={() => { setActiveCategory(""); load(q.trim(), city.trim(), locality.trim(), sector, ""); }}
              className={`px-3 py-1.5 rounded-sm border text-[11px] uppercase tracking-wider transition-colors ${!activeCategory ? "bg-[var(--ag-gold)] text-black border-transparent font-bold" : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
              data-testid="marketplace-cat-all"
            >Todos</button>
            {nonEmpty.map((c) => {
              const I = CAT_ICON(c.icon);
              const active = activeCategory === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => { setActiveCategory(c.key); load(q.trim(), city.trim(), locality.trim(), sector, c.key); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-[11px] uppercase tracking-wider transition-colors ${active ? "bg-[var(--ag-gold)] text-black border-transparent font-bold" : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
                  data-testid={`marketplace-cat-${c.key}`}
                  title={`${c.count} centros`}
                >
                  <I size={12} weight="fill" />
                  {c.label}
                  <span className="opacity-70 ml-0.5">({c.count})</span>
                </button>
              );
            })}
          </div>
        </section>
        );
      })()}

      {/* Sector tabs · grandes categorías */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="flex flex-wrap items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1" data-testid="marketplace-sector-tabs">
          {(() => {
            const groups = [
              { id: "", label: "Todos", icons: "🌐" },
              { id: "wash:", label: "Detailing & estética", match: (s) => /wash|detail|lavad/i.test(s.id) || /lavad|detail/i.test(s.label_es || "") },
              { id: "mech:", label: "Talleres Mecánicos", match: (s) => /mech|mecan/i.test(s.id) || /mec[aá]nic/i.test(s.label_es || "") },
              { id: "body:", label: "Chapa y Pintura", match: (s) => /body|chapa|paint|pintur/i.test(s.id) || /chapa|pintur/i.test(s.label_es || "") },
              { id: "tire:", label: "Neumáticos / ITV", match: (s) => /tire|neumat|wheel|itv|alin/i.test(s.id) || /neum|itv|aline/i.test(s.label_es || "") },
              { id: "other:", label: "Otros servicios", match: (s) => true },
            ];
            const usedSectorIds = new Set();
            const tabs = groups.map((g) => {
              if (!g.id) return { ...g, sectorIds: [] };
              const matched = sectors.filter((s) => g.match(s) && !usedSectorIds.has(s.id));
              matched.forEach((s) => usedSectorIds.add(s.id));
              return { ...g, sectorIds: matched.map((s) => s.id) };
            });
            const isActive = (g) => {
              if (!g.id) return !sector;
              return g.sectorIds.includes(sector);
            };
            return tabs.map((g) => (
              <button
                key={g.id || "all"}
                type="button"
                onClick={() => {
                  const next = !g.id ? "" : (g.sectorIds[0] || "");
                  setSector(next);
                  setTimeout(() => load(q.trim(), city.trim(), locality.trim(), next, activeCategory), 0);
                }}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded-sm border transition-colors ${
                  isActive(g)
                    ? "bg-[var(--ag-gold)] text-black border-transparent font-bold"
                    : "border-[var(--ag-border)] bg-transparent text-[var(--ag-text-dim)] hover:text-white"
                }`}
                data-testid={`marketplace-tab-${g.id || "all"}`}
              >
                {g.label}
              </button>
            ));
          })()}

          {/* List/Map toggle (Sprint S-5) */}
          <div className="ml-auto inline-flex items-center rounded-sm border border-[var(--ag-border)] overflow-hidden">
            <button
              onClick={() => setView("list")}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider inline-flex items-center gap-1.5 ${view === "list" ? "bg-[var(--ag-gold)] text-black font-bold" : "text-[var(--ag-text-dim)] hover:text-white"}`}
              data-testid="marketplace-view-list"
            >
              <ListBullets size={12} weight="bold" /> Lista
            </button>
            <button
              onClick={() => setView("map")}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider inline-flex items-center gap-1.5 ${view === "map" ? "bg-[var(--ag-gold)] text-black font-bold" : "text-[var(--ag-text-dim)] hover:text-white"}`}
              data-testid="marketplace-view-map"
            >
              <MapTrifold size={12} weight="bold" /> Mapa
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-14">
        <div className="flex items-center justify-between mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ag-text-dim)]" data-testid="marketplace-total">
            {loading ? "Cargando…" : `${total} centro${total === 1 ? "" : "s"} disponibles`}
          </div>
          {favs.size > 0 && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ag-gold)] inline-flex items-center gap-1.5" data-testid="marketplace-fav-count">
              <Heart size={12} weight="fill" /> {favs.size} favorito{favs.size === 1 ? "" : "s"}
            </div>
          )}
        </div>

        {view === "map" ? (
          <div className="ag-card overflow-hidden" data-testid="marketplace-map">
            <div className="h-[60vh] min-h-[420px] w-full">
              <MapContainer center={mapCenter} zoom={mapItems.length ? 11 : 6} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {mapItems.map((o) => (
                  <Marker key={o.id} position={[o.latitude, o.longitude]}>
                    <Popup>
                      <div style={{ minWidth: 200 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{o.name}</div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{[o.locality, o.city].filter(Boolean).join(" · ")}</div>
                        {o.rating_count > 0 && (
                          <div style={{ fontSize: 12, marginBottom: 6 }}>★ {o.rating_avg.toFixed(1)} ({o.rating_count})</div>
                        )}
                        <Link to={`/reservar/${o.slug}`} style={{ color: "var(--ag-gold)", fontWeight: 700, fontSize: 12 }}>Ver perfil →</Link>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
            {mapItems.length === 0 && (
              <div className="px-5 py-4 text-center text-xs text-[var(--ag-text-dim)] border-t border-[var(--ag-border)]">
                Aún no hay centros geolocalizados en los filtros actuales. Volver a <button className="underline text-[var(--ag-text)]" onClick={() => setView("list")}>vista de lista</button>.
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="ag-card p-5 animate-pulse">
                <div className="h-40 bg-[var(--ag-midnight)] rounded mb-4" />
                <div className="h-5 w-2/3 bg-[var(--ag-midnight)] rounded mb-2" />
                <div className="h-3 w-full bg-[var(--ag-midnight)] rounded" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyStateAddBusiness query={q} city={city} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
            {items.map((o) => {
              const socials = [];
              if (o.instagram_url) socials.push({ key: "instagram", icon: InstagramLogo, url: o.instagram_url, label: "Instagram", color: "#E1306C" });
              if (o.facebook_url) socials.push({ key: "facebook", icon: FacebookLogo, url: o.facebook_url, label: "Facebook", color: "#1877F2" });
              if (o.google_maps_url) socials.push({ key: "google_maps", icon: GoogleLogo, url: o.google_maps_url, label: "Google Maps", color: "#4285F4" });
              const isFav = favs.has(o.id);
              const inCompare = isInCompare(o.id);
              const availableToday = availability[o.id];
              return (
              <div key={o.id}
                className="ag-card overflow-hidden hover:border-[var(--ag-gold)]/70 hover:shadow-[0_8px_30px_rgba(255,183,3,0.12)] hover:-translate-y-0.5 transition-all duration-300 group flex flex-col relative"
                data-testid={`marketplace-card-${o.slug}`}>
                {/* Stripe superior con el color corporativo del centro */}
                <div className="absolute top-0 left-0 right-0 h-1 z-10" style={{ backgroundColor: o.brand_color || "#0033A0" }} aria-hidden />

                {/* Botones flotantes top-right: favorito + comparar */}
                <div className="absolute top-2.5 right-2.5 z-20 flex gap-1.5">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(o.id); track("marketplace_fav_toggle", { org: o.id, fav: !isFav }); }}
                    className={`w-8 h-8 grid place-items-center rounded-full backdrop-blur-md transition-all border ${isFav ? "bg-[var(--ag-gold)] text-black border-transparent" : "bg-black/55 text-white/85 border-white/15 hover:text-[var(--ag-gold)]"}`}
                    title={isFav ? "Quitar de favoritos" : "Añadir a favoritos"}
                    aria-label={isFav ? "Quitar de favoritos" : "Añadir a favoritos"}
                    data-testid={`marketplace-fav-${o.slug}`}
                  >
                    <Heart size={14} weight={isFav ? "fill" : "regular"} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleCompare(o.id); track("marketplace_compare_toggle", { org: o.id, on: !inCompare }); }}
                    className={`w-8 h-8 grid place-items-center rounded-full backdrop-blur-md transition-all border ${inCompare ? "bg-[var(--ag-gold)] text-black border-transparent" : "bg-black/55 text-white/85 border-white/15 hover:text-[var(--ag-gold)]"}`}
                    title={inCompare ? "Quitar de comparativa" : "Añadir a comparativa"}
                    aria-label={inCompare ? "Quitar de comparativa" : "Añadir a comparativa"}
                    data-testid={`marketplace-compare-${o.slug}`}
                  >
                    <Scales size={14} weight={inCompare ? "fill" : "regular"} />
                  </button>
                </div>

                {/* Hero image */}
                <Link to={`/reservar/${o.slug}`} className="block relative">
                  <div
                    className="h-52 bg-[var(--ag-midnight)] relative overflow-hidden"
                    style={o.hero_image_url ? {
                      backgroundImage: `linear-gradient(to bottom, rgba(26,26,26,0.05) 30%, rgba(26,26,26,0.85) 100%), url("${o.hero_image_url}")`,
                      backgroundSize: "cover", backgroundPosition: "center",
                    } : {}}
                  >
                    {!o.hero_image_url && (() => {
                      // Sprint P.1.5 · Bloque K (CP4) · Placeholder por sector.
                      const Ico = sectorPlaceholderIcon(o);
                      return (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--ag-gold)] opacity-30 gap-1.5" data-testid={`marketplace-sector-placeholder-${o.slug}`}>
                          <Ico size={64} weight="duotone" />
                          {(o.sectors && o.sectors[0]) && (
                            <span className="text-[9px] uppercase tracking-[0.2em] opacity-80">
                              {typeof o.sectors[0] === "string" ? o.sectors[0] : (o.sectors[0].label_es || o.sectors[0].id || "")}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    <div className="absolute bottom-3 left-4 flex items-center gap-3">
                      {o.logo_url ? (
                        <img src={o.logo_url} alt={o.name} className="w-14 h-14 rounded-lg object-contain bg-white p-1.5 border border-white/30 shadow-xl" />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-[var(--ag-gold)] text-[var(--ag-midnight)] grid place-items-center font-display font-black text-2xl border border-white/30 shadow-xl">
                          {o.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    {/* Banner vacaciones */}
                    {o.vacation_mode_active && (
                      <div className="absolute top-3 left-3 bg-amber-500/95 text-white text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-full shadow-lg" data-testid={`mp-vacation-${o.slug}`}>
                        En vacaciones
                      </div>
                    )}
                    {/* Sin reclamar — prioridad sobre "Disponible hoy" para perfiles freemium */}
                    {o.claimable && !o.vacation_mode_active && (
                      <div className="absolute top-3 left-3 bg-amber-300/95 text-[#0a0d12] text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-full shadow-lg inline-flex items-center gap-1" data-testid={`mp-claimable-${o.slug}`}>
                        <Crown size={11} weight="fill" /> Sin reclamar
                      </div>
                    )}
                    {/* Available today badge — solo si NO es claimable y NO está en vacaciones */}
                    {availableToday && !o.vacation_mode_active && !o.claimable && (
                      <div className="absolute top-3 left-3 bg-emerald-500/95 text-white text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-full shadow-lg inline-flex items-center gap-1" data-testid={`mp-avtoday-${o.slug}`}>
                        <CheckCircle size={11} weight="fill" /> Disponible hoy
                      </div>
                    )}
                    {/* Gallery counter top-right (movido más abajo por los nuevos botones) */}
                    {(o.gallery?.length || 0) > 0 && (
                      <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm border border-white/15 text-white text-[10px] px-2.5 py-1 rounded-full inline-flex items-center gap-1.5">
                        <Images size={11} weight="fill" /> {o.gallery.length}
                      </div>
                    )}
                  </div>
                </Link>

                <div className="px-5 pt-5 pb-4 flex flex-col gap-3.5 flex-1">
                  <Link to={`/reservar/${o.slug}`} className="block">
                    <div className="font-display font-extrabold text-[1.35rem] leading-tight tracking-tight group-hover:text-[var(--ag-gold)] transition-colors">{o.name}</div>
                    {(o.city || o.locality) && (
                      <div className="text-[11px] text-[var(--ag-text-dim)] mt-1.5 flex items-center gap-1.5 uppercase tracking-wider font-medium">
                        <MapPin size={11} weight="duotone" /> {[o.locality, o.city].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div className="mt-2.5">
                      {o.rating_count > 0 ? (
                        <StarRating value={o.rating_avg} count={o.rating_count} size={12} />
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]/55">Aún sin reseñas</span>
                      )}
                    </div>
                  </Link>
                  <div className="h-px bg-[var(--ag-border)]/60" />
                  {o.description && (
                    <p className="text-[13px] text-[var(--ag-text-dim)] leading-relaxed line-clamp-2">{o.description}</p>
                  )}
                  {/* Horario del día — solo si el centro lo gestiona realmente */}
                  {(() => {
                    if (!o.has_hours_configured) {
                      return (
                        <div className="flex items-center gap-2 text-[11px]" data-testid={`marketplace-hours-${o.slug}`}>
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider text-[10px] bg-white/5 text-[var(--ag-text-dim)] border border-[var(--ag-border)]">
                            Horario no disponible
                          </span>
                        </div>
                      );
                    }
                    const today = ["sun","mon","tue","wed","thu","fri","sat"][new Date().getDay()];
                    const perDay = o.opening_hours_per_day || {};
                    const todayHours = perDay[today];
                    let label = null;
                    let isOpen = false;
                    if (todayHours && !todayHours.closed && todayHours.open && todayHours.close) {
                      const open = todayHours.open;
                      const close = todayHours.close;
                      label = `${open} – ${close}`;
                      const now = new Date();
                      const minutes = now.getHours() * 60 + now.getMinutes();
                      const [oh, om] = open.split(":").map(Number);
                      const [ch, cm] = close.split(":").map(Number);
                      isOpen = minutes >= (oh * 60 + om) && minutes <= (ch * 60 + cm);
                    } else if (todayHours?.closed) {
                      label = "Hoy cerrado";
                    }
                    // Sprint P.1.5 · Bloque D · Fix P0-F · NUNCA fabricar horario
                    // a partir de `working_hours_start/end` legacy (defaults del SaaS)
                    // porque genera el fake público "09:00 – 20:00" para orgs sin
                    // horario real configurado. Si no hay `opening_hours_per_day` real,
                    // has_hours_configured=false en el backend y ya salimos arriba con
                    // el chip "Horario no disponible".
                    return label ? (
                      <div className="flex items-center gap-2 text-[11px]" data-testid={`marketplace-hours-${o.slug}`}>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider text-[10px] ${isOpen ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-400/40' : 'bg-red-500/15 text-red-400 border border-red-400/40'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-emerald-400' : 'bg-red-400'} animate-pulse`}></span>
                          {isOpen ? 'Abierto' : 'Cerrado'}
                        </span>
                        <span className="text-[var(--ag-text-dim)] font-mono-tab">{label}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-[11px]" data-testid={`marketplace-hours-${o.slug}`}>
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider text-[10px] bg-white/5 text-[var(--ag-text-dim)] border border-[var(--ag-border)]">
                          Horario no disponible
                        </span>
                      </div>
                    );
                  })()}
                  {socials.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`marketplace-socials-${o.slug}`}>
                      {socials.map((s) => (
                        <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer"
                          onClick={(e)=>e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[var(--ag-midnight-soft)]/70 border border-[var(--ag-border)] rounded-md text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/70 hover:text-white hover:bg-[var(--ag-midnight)] transition-all"
                          title={`Ver ${s.label}`} data-testid={`marketplace-social-${s.key}-${o.slug}`}>
                          <s.icon size={11} weight="fill" style={{ color: s.color }} />
                          {s.label}
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="mt-auto pt-3.5 border-t border-[var(--ag-border)] flex items-center justify-between gap-2">
                    {/* Auditoría SEO: enlace interno crawleable a la landing /centro/:slug */}
                    <Link to={`/centro/${o.slug}`}
                      className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[var(--ag-text-dim)] hover:text-white transition-colors inline-flex items-center gap-1"
                      data-testid={`marketplace-centro-link-${o.slug}`}>
                      <Storefront size={11} weight="duotone" /> Ver centro
                    </Link>
                    {o.claimable ? (
                      <Link to={`/reclamar/${o.slug}`}
                        className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-300 hover:text-amber-200 inline-flex items-center gap-2 group-hover:gap-3 transition-all"
                        data-testid={`marketplace-cta-claim-${o.slug}`}
                        title="Toma el control de este perfil · 30 días de prueba gratis">
                        <Crown size={12} weight="fill" />
                        <span>Reclamar</span>
                        <ArrowRight size={13} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ) : (
                      <Link to={`/reservar/${o.slug}`}
                        className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ag-gold)] inline-flex items-center gap-2 group-hover:gap-3 transition-all"
                        data-testid={`marketplace-cta-${o.slug}`}>
                        <span>Reservar</span>
                        <ArrowRight size={13} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    )}
                  </div>
                  {user && user.organization_id && user.organization_id !== o.id && (
                    <Link to={`/reservar/${o.slug}?as_org=1`}
                      className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border border-[var(--ag-gold)]/50 text-[#3b82f6] hover:bg-[var(--ag-gold)]/15 transition-colors w-fit"
                      data-testid={`marketplace-cta-b2b-${o.slug}`}
                      onClick={(e)=>e.stopPropagation()}
                      title="Reservar usando los datos fiscales de mi empresa"
                    >
                      <Briefcase size={11} weight="fill" /> Reservar como mi empresa
                    </Link>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Auditoría SEO · Enlaces internos a páginas de ciudad (/ciudad/:city) */}
      {cities.length > 0 && (
        <section className="border-t border-[var(--ag-border)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="marketplace-city-links">
            <h2 className="text-[10px] uppercase tracking-[0.25em] text-[var(--ag-text-dim)] font-bold mb-4 flex items-center gap-2">
              <MapPin size={12} weight="duotone" /> Explora centros por ciudad
            </h2>
            <div className="flex flex-wrap gap-2">
              {cities.map((c) => (
                <Link
                  key={c}
                  to={`/ciudad/${slugifyCity(c)}`}
                  className="px-3 py-1.5 rounded-sm border border-[var(--ag-border)] text-[11px] uppercase tracking-wider text-[var(--ag-text-dim)] hover:text-white hover:border-[var(--ag-gold)]/70 transition-colors"
                  data-testid={`marketplace-city-link-${slugifyCity(c)}`}
                >
                  {c}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA pro */}
      <section className="border-t border-[var(--ag-border)] bg-[var(--ag-midnight-soft)]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
          <Sparkle size={32} weight="fill" className="text-[var(--ag-gold)] mx-auto" />
          <h2 className="font-display font-black text-3xl mt-5 tracking-tighter">¿Tienes un centro y quieres aparecer aquí?</h2>
          <p className="text-[var(--ag-text-dim)] mt-3">Crea tu perfil en AutoGlow y empieza a recibir reservas online en menos de 90 segundos.</p>
          <Button onClick={()=>navigate("/register")} className="ag-btn-gold mt-6 h-11 px-6">
            <Storefront className="mr-2" weight="fill" /> Probar AutoGlow gratis · 30 días
          </Button>
        </div>
      </section>

      {/* Sprint S-5 · Sticky compare bar */}
      {compareIds.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-xl" data-testid="marketplace-compare-bar">
          <div className="bg-[var(--ag-midnight-soft)] border border-[var(--ag-gold)]/50 shadow-2xl shadow-black/40 rounded-lg p-3 flex items-center justify-between gap-3 backdrop-blur-md">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--ag-gold)] inline-flex items-center gap-2">
              <Scales size={14} weight="fill" /> {compareIds.length}/3 comparando
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={clearCompare} className="h-8 text-[11px] text-[var(--ag-text-dim)] hover:text-white" data-testid="marketplace-compare-clear">
                Vaciar
              </Button>
              <Button onClick={openCompare} className="ag-btn-gold h-8 text-[11px] px-4" data-testid="marketplace-compare-open">
                Comparar ahora
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Compare modal */}
      {showCompareModal && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={()=>setShowCompareModal(false)}>
          <div className="ag-card max-w-5xl w-full max-h-[85vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()} data-testid="marketplace-compare-modal">
            <div className="flex items-center justify-between p-5 border-b border-[var(--ag-border)] sticky top-0 bg-[var(--ag-midnight-soft)] z-10">
              <h3 className="font-display font-black text-2xl tracking-tight">Comparativa</h3>
              <button onClick={()=>setShowCompareModal(false)} className="text-[var(--ag-text-dim)] hover:text-white" aria-label="Cerrar" data-testid="marketplace-compare-close">
                <X size={20} />
              </button>
            </div>
            <div className="grid" style={{ gridTemplateColumns: `repeat(${compareData.length}, minmax(0,1fr))` }}>
              {compareData.map((o) => (
                <div key={o.id} className="border-r border-[var(--ag-border)] last:border-r-0 p-5 flex flex-col gap-3">
                  <div className="h-32 rounded-md bg-[var(--ag-midnight)] overflow-hidden relative"
                       style={o.hero_image_url ? { backgroundImage: `url("${o.hero_image_url}")`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
                    {!o.hero_image_url && (<div className="absolute inset-0 grid place-items-center text-[var(--ag-gold)] opacity-30"><Storefront size={36} weight="duotone" /></div>)}
                  </div>
                  <div>
                    <div className="font-display font-extrabold text-lg leading-tight">{o.name}</div>
                    <div className="text-[11px] text-[var(--ag-text-dim)] uppercase tracking-wider mt-1">{o.city}</div>
                  </div>
                  <div className="text-[12px] space-y-2">
                    <Row label="Valoración" value={o.rating_count > 0 ? `★ ${o.rating_avg.toFixed(1)} (${o.rating_count})` : "Sin reseñas"} />
                    <Row label="Sectores" value={(o.sectors || []).slice(0, 3).join(", ") || "—"} />
                    <Row label="Servicios" value={`${o.catalog_count} en catálogo`} />
                    <Row label="Desde" value={o.min_price != null ? `${o.min_price.toFixed(2)} €` : "—"} />
                    <Row label="Disponible hoy" value={o.available_today ? "Sí ✓" : "No"} />
                  </div>
                  {o.top_services?.length > 0 && (
                    <div className="text-[11px] text-[var(--ag-text-dim)]">
                      <div className="uppercase tracking-wider mb-1.5">Top servicios</div>
                      <ul className="space-y-0.5">
                        {o.top_services.map((s, i) => (<li key={i} className="flex items-start gap-1.5"><Sparkle size={9} weight="fill" className="text-[var(--ag-gold)] mt-1" /><span>{s}</span></li>))}
                      </ul>
                    </div>
                  )}
                  <Link to={`/reservar/${o.slug}`} className="ag-btn-gold h-9 mt-auto text-[11px] uppercase tracking-wider font-bold inline-flex items-center justify-center rounded-md" data-testid={`marketplace-compare-cta-${o.slug}`}>
                    Ver perfil <ArrowRight size={12} weight="bold" className="ml-1.5" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-[var(--ag-border)]/40 pb-1.5">
      <span className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

// Fase 2A · P0-2 · Mobile filter body (contents of the Sheet)
function MobileFiltersBody({
  cities, localities, sectors,
  city, setCity, locality, setLocality, sector, setSector,
  sortBy, setSortBy, total, onApply, onClear,
}) {
  const [cityQuery, setCityQuery] = useState(city || "");
  const cityMatches = useMemo(() => {
    const term = cityQuery.trim().toLowerCase();
    if (!term || term === (city || "").toLowerCase()) return (cities || []).slice(0, 10);
    return (cities || []).filter((c) => c.toLowerCase().includes(term)).slice(0, 10);
  }, [cityQuery, cities, city]);

  const SORT_OPTIONS = [
    { key: "relevance", label: "Relevancia" },
    { key: "rating", label: "Mejor valorados" },
    { key: "recent", label: "Más recientes" },
  ];

  return (
    <div className="pt-4 pb-6 space-y-6">
      {/* Ciudad · typeahead */}
      <div data-testid="mobile-filter-city-block">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-2 flex items-center gap-1.5">
          <MapPin size={11} weight="bold" /> Ciudad
        </div>
        <div className="relative">
          <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)] pointer-events-none" />
          <Input
            value={cityQuery}
            onChange={(e) => setCityQuery(e.target.value)}
            placeholder="Empieza a escribir…"
            className="pl-9"
            data-testid="mobile-filter-city-input"
          />
          {cityQuery && (
            <button
              type="button"
              onClick={() => { setCityQuery(""); setCity(""); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)] hover:text-white p-1"
              data-testid="mobile-filter-city-clear"
              aria-label="Limpiar ciudad"
            >
              <X size={12} />
            </button>
          )}
        </div>
        {cityMatches.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="mobile-filter-city-suggestions">
            {cityMatches.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => { setCity(c); setCityQuery(c); }}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                  c === city
                    ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] border-[var(--ag-gold)] font-bold"
                    : "bg-transparent text-[var(--ag-text-dim)] border-[var(--ag-border)] hover:text-white"
                }`}
                data-testid={`mobile-filter-city-chip-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Localidad · solo si hay ciudad */}
      {city && localities && localities.length > 0 && (
        <div data-testid="mobile-filter-locality-block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-2">
            Localidad
          </div>
          <select
            value={locality}
            onChange={(e) => setLocality(e.target.value)}
            className="w-full px-3 h-10 bg-[var(--ag-midnight-soft)] text-[var(--ag-text)] border border-[var(--ag-border)] rounded outline-none focus:ring-1 focus:ring-[var(--ag-gold)] text-sm"
            data-testid="mobile-filter-locality"
          >
            <option value="">Toda la localidad</option>
            {localities.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
      )}

      {/* Sector · chips */}
      <div data-testid="mobile-filter-sector-block">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-2 flex items-center gap-1.5">
          <Funnel size={11} weight="bold" /> Sector
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setSector("")}
            className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
              !sector
                ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] border-[var(--ag-gold)] font-bold"
                : "bg-transparent text-[var(--ag-text-dim)] border-[var(--ag-border)] hover:text-white"
            }`}
            data-testid="mobile-filter-sector-all"
          >
            Todos
          </button>
          {(sectors || []).slice(0, 30).map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSector(s.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                sector === s.id
                  ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] border-[var(--ag-gold)] font-bold"
                  : "bg-transparent text-[var(--ag-text-dim)] border-[var(--ag-border)] hover:text-white"
              }`}
              data-testid={`mobile-filter-sector-${s.id}`}
            >
              {s.label_es}
            </button>
          ))}
        </div>
      </div>

      {/* Ordenar por · radio */}
      <div data-testid="mobile-filter-sort-block">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-2">
          Ordenar por
        </div>
        <div className="space-y-1">
          {SORT_OPTIONS.map((o) => (
            <label
              key={o.key}
              className="flex items-center gap-3 px-3 py-2 border border-[var(--ag-border)] rounded cursor-pointer hover:bg-[var(--ag-midnight-soft)]"
              data-testid={`mobile-filter-sort-${o.key}`}
            >
              <input
                type="radio"
                name="marketplace-sort"
                checked={sortBy === o.key}
                onChange={() => setSortBy(o.key)}
                className="accent-[var(--ag-gold)]"
              />
              <span className="text-sm">{o.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* CTAs sticky */}
      <div className="flex items-center gap-2 pt-2 sticky bottom-0 bg-[var(--ag-midnight)] pb-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClear}
          className="flex-1 bg-transparent border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"
          data-testid="mobile-filter-clear"
        >
          Limpiar
        </Button>
        <Button
          type="button"
          onClick={onApply}
          className="flex-1 ag-btn-gold"
          data-testid="mobile-filter-apply"
        >
          Aplicar {total > 0 ? `· ${total}` : ""}
        </Button>
      </div>
    </div>
  );
}
